import React, { useEffect } from 'react';
import { useLocale } from 'locale';
import { IFiltersState } from 'components/filters/types';
import Modal from 'ds/components/modal';
import { H3, Text } from 'ds/components/typography';
import InfoSignIcon from 'assets/svg/private-bulletin-promotion/modal-info-sign.svg';
import { ActionsWrapper, CancelButton, Content } from './styled';
import { addSaveSearchData, findSaveSearchFiltersSelector, previousSavedSearchSelector } from 'store/state/selectors/savedSearchPage';
import { State } from 'store/state';
import { connect } from 'react-redux';
import { Button } from 'ds/components/button';
import { ISaveSearchFromState } from 'store/sagas/apiService/types';
import FilterThumbnails from 'components/filters/thumbnails';
import { FiltersWrapper } from '../post-saved-modal/styled';
import * as actions from 'store/state/app/actions';
import { UpdateFrequency } from '../config';
import { ModalIconWrapper, ModalTitle } from '../shared/modal-styles';
import { getMultiLocationTitle } from '../shared/formatters';
import { BannerType } from 'components/notification-banner/bannersConfig';
import { useSendEvent } from 'analytics/useSendEvent';

interface ExistingSearchModalProps {
  onClose: () => void;
  onCreate: () => void;
  onUpdate: () => void;
  onCreated: () => void;
  onUpdated: () => void;
  filters: Partial<IFiltersState>;
  isDesktop?: boolean;
  saveData?: ISaveSearchFromState;
}

const ExistingSearchModal: React.FC<ExistingSearchModalProps> = ({
  onClose,
  onCreate,
  onUpdate,
  filters,
  isDesktop,
  saveData,
}) => {
  const { t } = useLocale();
  const multiLocationTitle = getMultiLocationTitle(saveData, t);
  const sendEvent = useSendEvent();

  useEffect(() => {
    sendEvent('popup_exposed', 'popup', {
      event: {
        source: 'market_place',
        type: 'saved_search_similar_search_found',
      },
    });
  }, []);

  const handleClose = () => {
    sendEvent('popup_close', 'popup', {
      event: {
        source: 'market_place',
        type: 'saved_search_similar_search_found',
      },
    });
    onClose();
  };

  return (
    <Modal
      desktopModalPositionCenter={isDesktop}
      mobileModalPositionBottom={!isDesktop}
      contentWrapperStyle={isDesktop ? { maxWidth: 500 } : undefined}
      isOpen
      onClose={handleClose}
    >
      <Content>
        <ModalIconWrapper>
          <InfoSignIcon width={88} height={88} />
        </ModalIconWrapper>
        <ModalTitle>
          <H3 weight="bold">{t('existingSearchModal.title')}</H3>
          <Text>{t('existingSearchModal.subtitle')}</Text>
        </ModalTitle>
        <FiltersWrapper>
          <H3 weight="bold">{multiLocationTitle}</H3>
          <FilterThumbnails showDealType filters={filters} />
        </FiltersWrapper>
      </Content>
      <ActionsWrapper>
        <Button size="extraLarge" onClick={onUpdate}>{t('existingSearchModal.updateExisting')}</Button>
        <CancelButton size="large" mode="ghost" onClick={onCreate}>{t('existingSearchModal.createNew')}</CancelButton>
      </ActionsWrapper>
    </Modal>
  );
};

const mapStateToProps = (state: State) => ({
  saveData: addSaveSearchData(state),
  existingSavedSearch: previousSavedSearchSelector(state),
  filters: findSaveSearchFiltersSelector(state),
});

const mapDispatchToProps = {
  updateSaveSearch: actions.updateSaveSearch,
  saveSearch: actions.saveSearch,
  setNotificationBannerMessage: actions.setNotificationBannerMessage,
};

type ExistingSearchModalOwnProps = Pick<ExistingSearchModalProps, 'onClose' | 'isDesktop' | 'onCreated' | 'onUpdated'>;

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  (stateProps: ReturnType<typeof mapStateToProps>, dispatchProps, ownProps: ExistingSearchModalOwnProps) => ({
    ...stateProps,
    ...ownProps,
    onCreate() {
      dispatchProps.saveSearch({
        ...stateProps.saveData,
        updateFrequency: UpdateFrequency.Daily,
      }, { source: 'market_place', saveSilently: true });
      ownProps.onClose();
      ownProps.onCreated();
    },
    onUpdate() {
      dispatchProps.updateSaveSearch({
        ...stateProps.saveData,
        updateFrequency: stateProps.existingSavedSearch.updateFrequency,
        searchId: stateProps.existingSavedSearch.searchId,
      });
      dispatchProps.setNotificationBannerMessage({
        typeId: BannerType.SearchSaved,
        timeout: 5000,
      });
      ownProps.onClose();
      ownProps.onUpdated();
    },
  })
)(ExistingSearchModal);
